import type ButtonProps from '@aurora/external-types/components/common/Button/ButtonProps';
import {
  ButtonVariant,
  LoadingButtonVariant
} from '@aurora/external-types/components/common/Button/enums';
import {
  IconColor,
  IconSize,
  IconTypes
} from '@aurora/external-types/components/common/Icon/enums';
import {
  LoadingVariant,
  LoadingSize,
  LoadingSpacing,
  LoadingTheme
} from '@aurora/external-types/components/common/Loading/enums';
import type LoadingProps from '@aurora/external-types/components/common/Loading/LoadingProps';
import { PanelSize, PanelType } from '@aurora/external-types/components/common/Panel/enums';
import type UserAvatarProps from '@aurora/external-types/components/users/UserAvatar/UserAvatarProps';
import { TextAlignment } from '@aurora/external-types/utils/I18n/enums';
import type {
  ModalProps,
  DropdownProps,
  DropdownButtonProps,
  FormCheckProps,
  FormLabelProps,
  FormTextProps,
  FormProps,
  FormControlProps,
  InputGroupProps,
  ListGroupProps,
  ListGroupItemProps,
  AccordionCollapseProps,
  AccordionProps,
  AccordionToggleProps,
  ProgressBarProps,
  TableProps,
  TooltipProps,
  TabContainerProps,
  TabPaneProps,
  TabProps
} from 'react-bootstrap';
import type { ModalDialogProps } from 'react-bootstrap/lib/esm/ModalDialog';
import type { ModalHeaderProps } from 'react-bootstrap/lib/esm/ModalHeader';
import type { DropdownItemProps } from 'react-bootstrap/lib/esm/DropdownItem';
import type { DropdownMenuProps } from 'react-bootstrap/lib/esm/DropdownMenu';
import type { DropdownToggleProps } from 'react-bootstrap/lib/esm/DropdownToggle';
import type NodeLinkProps from '@aurora/external-types/components/nodes/NodeLink/NodeLinkProps';
import type UserLinkProps from '@aurora/external-types/components/users/UserLink/UserLinkProps';
import {
  ToastAlertVariant,
  ToastVariant
} from '@aurora/external-types/components/common/ToastAlert/enums';
import { BannerAlertVariant } from '@aurora/external-types/components/common/BannerAlert/enums';
import dynamic from 'next/dynamic';
import type {
  ExternalComponents,
  ExternalComponentEnums
} from '@aurora/external-types/externalContext';
import type {
  PanelProps,
  PanelSectionProps
} from '@aurora/external-types/components/common/Panel/PanelProps';
import type { FormCheckInputProps } from 'react-bootstrap/lib/esm/FormCheckInput';
import type { FormCheckLabelProps } from 'react-bootstrap/lib/esm/FormCheckLabel';
import type IconProps from '@aurora/external-types/components/common/Icon/IconProps';
import type { OverlayProps } from 'react-bootstrap/lib/esm/Overlay';
import type { OverlayTriggerProps } from 'react-bootstrap/lib/esm/OverlayTrigger';
import type { BannerAlertProps } from '@aurora/external-types/components/common/BannerAlert/BannerAlertProps';

const Accordion = dynamic<AccordionProps>(() =>
  import('./bootstrap/ExternalAccordion').then(accordion => accordion.Accordion)
);
const AccordionCollapse = dynamic<AccordionCollapseProps>(() =>
  import('./bootstrap/ExternalAccordion').then(accordion => accordion.AccordionCollapse)
);
const AccordionToggle = dynamic<AccordionToggleProps>(() =>
  import('./bootstrap/ExternalAccordion').then(accordion => accordion.AccordionToggle)
);
const Form = dynamic<FormProps>(() => import('./bootstrap/ExternalForm'));
const FormCheck = dynamic<FormCheckProps>(() => import('./bootstrap/ExternalFormCheck'));
const FormCheckInput = dynamic<FormCheckInputProps>(
  () => import('./bootstrap/ExternalFormCheckInput')
);
const FormCheckLabel = dynamic<FormCheckLabelProps>(
  () => import('./bootstrap/ExternalFormCheckLabel')
);
const FormControl = dynamic<FormControlProps>(() => import('./bootstrap/ExternalFormControl'));
const FormLabel = dynamic<FormLabelProps>(() => import('./bootstrap/ExternalFormLabel'));
const FormText = dynamic<FormTextProps>(() => import('./bootstrap/ExternalFormText'));
const InputGroup = dynamic<InputGroupProps>(() =>
  import('./bootstrap/ExternalInputGroup').then(ig => ig.InputGroup)
);
const InputGroupAppend = dynamic<{}>(() =>
  import('./bootstrap/ExternalInputGroup').then(ig => ig.InputGroupAppend)
);
const InputGroupPrepend = dynamic<{}>(() =>
  import('./bootstrap/ExternalInputGroup').then(ig => ig.InputGroupPrepend)
);
const InputGroupText = dynamic<{}>(() =>
  import('./bootstrap/ExternalInputGroup').then(ig => ig.InputGroupText)
);
const InputGroupCheckbox = dynamic<{}>(() =>
  import('./bootstrap/ExternalInputGroup').then(ig => ig.InputGroupCheckbox)
);
const InputGroupRadio = dynamic<{}>(() =>
  import('./bootstrap/ExternalInputGroup').then(ig => ig.InputGroupRadio)
);
const Dropdown = dynamic<DropdownProps>(() => import('./bootstrap/ExternalDropdown'));
const DropdownButton = dynamic<DropdownButtonProps>(
  () => import('./bootstrap/ExternalDropdownButton')
);
const DropdownItem = dynamic<DropdownItemProps>(() => import('./bootstrap/ExternalDropdownItem'));
const DropdownMenu = dynamic<DropdownMenuProps>(() => import('./bootstrap/ExternalDropdownMenu'));
const DropdownToggle = dynamic<DropdownToggleProps>(
  () => import('./bootstrap/ExternalDropdownToggle')
);
const DropdownDivider = dynamic<{}>(() =>
  import('./bootstrap/ExternalDropdown').then(dropdown => dropdown.DropdownDivider)
);
const Button = dynamic<ButtonProps>(() => import('../components/common/Button/ExternalButton'));
const EmptyState = dynamic<ModalProps>(
  () => import('../components/common/EmptyState/ExternalEmptyState')
);
const Modal = dynamic<ModalProps>(() => import('./bootstrap/ExternalModal'));
const ModalBody = dynamic<{}>(() => import('./bootstrap/ExternalModalBody'));
const ModalDialog = dynamic<ModalDialogProps>(() => import('./bootstrap/ExternalModalDialog'));
const ModalFooter = dynamic<{}>(() => import('./bootstrap/ExternalModalFooter'));
const ModalHeader = dynamic<ModalHeaderProps>(() => import('./bootstrap/ExternalModalHeader'));
const ModalTitle = dynamic<{}>(() => import('./bootstrap/ExternalModalTitle'));
const NodeLink = dynamic<NodeLinkProps>(
  () => import('../components/nodes/NodeLink/ExternalNodeLink')
);
const Loading = dynamic<LoadingProps>(() => import('../components/common/Loading/ExternalLoading'));
const ListGroup = dynamic<ListGroupProps>(() =>
  import('./bootstrap/ExternalListGroup').then(lg => lg.ListGroup)
);
const ListGroupItem = dynamic<ListGroupItemProps>(() =>
  import('./bootstrap/ExternalListGroup').then(lg => lg.ListGroupItem)
);
const Overlay = dynamic<OverlayProps>(() => import('./bootstrap/ExternalOverlay'));
const OverlayTrigger = dynamic<OverlayTriggerProps>(
  () => import('./bootstrap/ExternalOverlayTrigger')
);
const Panel = dynamic<PanelProps>(() => import('../components/common/Panel/ExternalPanel'));
const PanelHeader = dynamic<PanelSectionProps>(() =>
  import('../components/common/Panel/ExternalPanel').then(panel => panel.PanelHeader)
);
const PanelBody = dynamic<PanelSectionProps>(() =>
  import('../components/common/Panel/ExternalPanel').then(panel => panel.PanelBody)
);
const PanelFooter = dynamic<PanelSectionProps>(() =>
  import('../components/common/Panel/ExternalPanel').then(panel => panel.PanelFooter)
);
const UserAvatar = dynamic<UserAvatarProps>(
  () => import('../components/users/UserAvatar/ExternalUserAvatar')
);
const UserLink = dynamic<UserLinkProps>(
  () => import('../components/users/UserLink/ExternalUserLink')
);
const ProgressBar = dynamic<ProgressBarProps>(() => import('./bootstrap/ExternalProgressBar'));
const Table = dynamic<TableProps>(() => import('./bootstrap/ExternalTable'));
const Tooltip = dynamic<TooltipProps>(() => import('./bootstrap/ExternalTooltip'));
const Icon = dynamic<IconProps>(() => import('../components/common/Icon/ExternalIcon'));
const BannerAlert = dynamic<BannerAlertProps>(
  () => import('../components/common/BannerAlert/ExternalBannerAlert')
);
const Tabs = dynamic<TabProps>(() => import('./bootstrap/ExternalTabs'));
const Tab = dynamic<TabProps>(() => import('./bootstrap/ExternalTab'));
const TabContainer = dynamic<TabContainerProps>(() =>
  import('./bootstrap/ExternalTab').then(t => t.Container)
);
const TabContent = dynamic<{}>(() => import('./bootstrap/ExternalTab').then(t => t.Content));
const TabPane = dynamic<TabPaneProps>(() => import('./bootstrap/ExternalTab').then(t => t.Pane));

const enums: ExternalComponentEnums = {
  ButtonVariant,
  IconSize,
  IconTypes,
  IconColor,
  LoadingSize,
  LoadingVariant,
  LoadingButtonVariant,
  LoadingSpacing,
  LoadingTheme,
  PanelSize,
  PanelType,
  TextAlignment,
  ToastAlertVariant,
  ToastVariant,
  BannerAlertVariant
};

const components: ExternalComponents = {
  Accordion,
  AccordionCollapse,
  AccordionToggle,
  BannerAlert,
  InputGroup,
  InputGroupAppend,
  InputGroupPrepend,
  InputGroupText,
  InputGroupCheckbox,
  InputGroupRadio,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownDivider,
  DropdownMenu,
  DropdownToggle,
  Button,
  EmptyState,
  Form,
  FormCheck,
  FormCheckInput,
  FormCheckLabel,
  FormControl,
  FormLabel,
  FormText,
  Loading,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalDialog,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  NodeLink,
  Overlay,
  OverlayTrigger,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  UserAvatar,
  UserLink,
  Icon,
  ProgressBar,
  Table,
  Tab,
  Tabs,
  TabContainer,
  TabContent,
  TabPane,
  Tooltip,
  enums
};

export default components;
